import { Label } from '@/components/ui/label';
import { Byproduct } from '@/graphql/graphql';
import { Download } from 'lucide-react';
import dayjs from 'dayjs';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

const formatBytes = (bytes?: number | null): string => {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatExpiry = (expiresAt: Date | string, t: (k: string, opts?: any) => string): string => {
  const d = dayjs(expiresAt);
  const now = dayjs();
  if (d.isBefore(now)) return t('batch.byproducts.expired');
  const hours = d.diff(now, 'hour');
  if (hours < 1) return t('batch.byproducts.expiresInMinutes', { count: Math.max(1, d.diff(now, 'minute')) });
  if (hours < 48) return t('batch.byproducts.expiresInHours', { count: hours });
  return t('batch.byproducts.expiresOn', { date: d.format('YYYY-MM-DD HH:mm') });
};

interface iByproductListProps {
  byproducts: Pick<
    Byproduct,
    'id' | 'filename' | 'type' | 'contentLength' | 'downloadUrl' | 'expiresAt'
  >[];
}

const ByproductList: FunctionComponent<iByproductListProps> = ({ byproducts }) => {
  const { t } = useTranslation('project');
  if (!byproducts.length) return null;
  return (
    <>
      <Label className='mt-8'>{t('batch.byproducts.title')}</Label>
      <div className='mt-2 border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 overflow-hidden'>
        {byproducts.map((bp) => (
          <div
            key={bp.id}
            className='flex items-center gap-3 px-3 py-2 border-b border-slate-200/70 last:border-b-0 dark:border-stone-800'>
            <div className='flex-1 min-w-0'>
              <div className='font-medium text-slate-900 dark:text-stone-100 truncate'>{bp.filename}</div>
              <div className='text-xs text-slate-500 dark:text-stone-400 flex items-center gap-2'>
                <span className='uppercase tracking-wide'>{bp.type}</span>
                <span>·</span>
                <span>{formatBytes(bp.contentLength ?? undefined)}</span>
                {bp.expiresAt && (
                  <>
                    <span>·</span>
                    <span className='text-amber-600 dark:text-amber-400'>{formatExpiry(bp.expiresAt, t)}</span>
                  </>
                )}
              </div>
            </div>
            <a
              href={bp.downloadUrl}
              download={bp.filename}
              className='inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30'>
              <Download className='size-3.5' />
              {t('batch.byproducts.download')}
            </a>
          </div>
        ))}
      </div>
    </>
  );
};

export default ByproductList;
